import React, { useState, useEffect } from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'
import MaterialIcon from '../materialIcon/materialIcon'
import { getFromApi } from 'actions'
import { VALUES } from 'utils/constants'
import { useStateValue } from 'context/store'
import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import RegulationCard from '../RegulationCard'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import {
   makeStyles,
   Grid,
   IconButton,
   Typography,
   Paper,
   Accordion,
   AccordionSummary,
   AccordionDetails,
   TablePagination,
   Radio,
   RadioGroup,
   FormControlLabel,
   FormControl,
   FormLabel,
   InputBase,
   Popover
} from '@material-ui/core'

const useRegulationsStyles = makeStyles(theme => ({
   viewContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontWeight: 600,
      fontSize: 14,
      color: '#43496a',
      width: '100%',
      height: '100%',
      padding: theme.spacing(2, 2, 0)
   },
   paper: {
      display: 'flex',
      width: '100%',
      height: '100%',
      borderRadius: 8,
      flexDirection: 'column',
      justifyContent: 'center'
   },
   popoverContent: {
      display: 'flex',
      width: '100%',
      borderTop: '4px solid #3264DC',
      borderRadius: 5,
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      justifyContent: 'space-between'
   },
   header: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(1.5, 2),
      alignItems: 'center',
      marginBottom: theme.spacing(1)
   },
   title: {
      fontSize: 18,
      fontWeight: 'bold'
   },
   iconsContainer: {
      display: 'flex',
      alignItems: 'center',
      width: 360
   },
   body: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column'
   },
   footer: {
      display: 'flex',
      justifyContent: 'flex-end'
   },
   button: {
      font: 'normal normal bold 12px/16px Roboto'
   },
   active: {
      color: '#3264DC'
   },
   filterIcon: {
      color: '#8A8FAB',
      padding: 0,
      marginLeft: theme.spacing(2)
   },
   regulationsContainer: {
      display: 'flex',
      flexDirection: 'column'
   },
   accordionMainTitle: {
      marginLeft: theme.spacing(1)
   },
   accordionWidth: {
      width: '100%'
   },
   notFoundContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
   }
}))

const INITAL_PAGINATION_DATA = {
   page: 0,
   size: 10,
   order: 'desc'
}

export function sortRegulationsByDate(left, right) {
   return moment(
      `${right.createdDate} ${right.hour}`,
      'DD-MM-YYYY hh:mm:ss'
   ).diff(moment(`${left.createdDate} ${left.hour}`, 'DD-MM-YYYY hh:mm:ss'))
}

export function sortRegulationsMap(regulationsData = []) {
   const map = {}
   regulationsData.forEach(regulation => {
      const { name: subdomainName, icon, id } = regulation.subdomain
      if (!map[subdomainName]) {
         map[subdomainName] = {
            id,
            icon,
            regulations: []
         }
      }
      map[subdomainName].regulations.push(regulation)
   })
   for (const subdomainName in map) {
      const { regulations } = map[subdomainName]
      regulations.sort(sortRegulationsByDate)
   }
   return map
}

function Regulations() {
   const classes = useRegulationsStyles()

   const [{ regulations = [] }, dispatch] = useStateValue()

   const [filtersAnchorEl, setFiltersAnchorEl] = useState(null)
   const [selectedFilter, setSelectedFilter] = useState('subdomain')
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(
      INITAL_PAGINATION_DATA
   )
   const [typedQuery, setTypedQuery] = useState(null)
   const [regulationsMap, setRegulationsMap] = useState({})
   const [regulationsList, setRegulationsList] = useState([])

   useEffect(() => {
      getRegulations()
   }, []) // eslint-disable-line react-hooks/exhaustive-deps

   useEffect(() => {
      if (regulations && regulations.length) {
         if (selectedFilter === 'subdomain') {
            const map = sortRegulationsMap(cloneDeep(regulations))
            setRegulationsMap(map)
         } else {
            const list = cloneDeep(regulations.sort(sortRegulationsByDate))
            setRegulationsList(list)
         }
      }
   }, [regulations])

   function handleFiltersClick(event) {
      setFiltersAnchorEl(event.currentTarget)
   }

   function onFilterSelection(value) {
      getRegulations(null, value, INITAL_PAGINATION_DATA)
      setFiltersAnchorEl(null)
   }

   async function _getRegulations(
      externalQuery,
      externalFilter,
      pagingFromTableParams
   ) {
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setPaginationParams(paginationFilters)
      const filter = externalFilter ?? selectedFilter
      setSelectedFilter(filter)
      const query = externalQuery ?? typedQuery
      setTypedQuery(query)
      let columnOrder = null
      if (filter === 'subdomain') {
         columnOrder = 'subdomain.name'
      }
      if (filter === 'date') {
         columnOrder = 'createdDate'
      }
      let read = null
      if (filter === 'read') {
         read = true
      }
      if (filter === 'unread') {
         read = false
      }
      const payload = {
         ...paginationFilters,
         query: query || null,
         columnOrder,
         read
      }
      const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/circulars-regulations/search`
      const {
         data: regulationsData,
         pageSize,
         totalRows,
         pageNumber
      } = await getFromApi(url, payload)
      dispatch({
         type: 'SET_ALL_REGULATIONS_LIST',
         regulationsData
      })
      setTableData({ pageSize, totalRows, pageNumber })
   }

   const getRegulations = useApiRequest(
      _getRegulations,
      'Historial Normativas: Consultar'
   )

   return (
      <Grid className={classes.viewContainer}>
         <Paper className={classes.paper}>
            <Grid className={classes.popoverContent}>
               <Grid className={classes.header}>
                  <Typography className={classes.title}>
                     Todas tus Circulares y Normativas
                  </Typography>
                  <Grid className={classes.iconsContainer}>
                     <SearchField onSearchCallback={getRegulations} />
                     <IconButton
                        className={classes.filterIcon}
                        onClick={handleFiltersClick}
                     >
                        <FilterListIcon />
                     </IconButton>
                     <RegulationsFiltersPopover
                        value={selectedFilter}
                        onFilterSelection={onFilterSelection}
                        anchorEl={filtersAnchorEl}
                        setAnchorEl={setFiltersAnchorEl}
                     />
                  </Grid>
               </Grid>
               <Grid className={classes.body}>
                  {selectedFilter === 'subdomain' ? (
                     <RegulationsAccordeon regulationsMap={regulationsMap} />
                  ) : (
                     <RegulationsList regulationsList={regulationsList} />
                  )}
               </Grid>
               <Grid className={classes.footer}>
                  <TablePagination
                     rowsPerPage={tableData.pageSize}
                     count={tableData.totalRows}
                     page={tableData.pageNumber}
                     onChangePage={(_, page) =>
                        getRegulations(null, null, { page })
                     }
                     onChangeRowsPerPage={e => {
                        getRegulations(null, null, {
                           size: e.target.value
                        })
                     }}
                     rowsPerPageOptions={[5, 10, 15, 20]}
                  />
               </Grid>
            </Grid>
         </Paper>
      </Grid>
   )
}

export default Regulations

// -----------------------

function NoResultsMessage() {
   const classes = useRegulationsStyles()
   return (
      <Grid className={classes.notFoundContainer}>
         <Typography>No hay coincidecia para esta consulta</Typography>
      </Grid>
   )
}

// ----------------------

function RegulationsAccordeon({ regulationsMap = {} }) {
   const classes = useRegulationsStyles()

   if (!Object.keys(regulationsMap).length) {
      return <NoResultsMessage />
   }

   return (
      <>
         {Object.keys(regulationsMap).map(subdomainName => {
            const { regulations, icon, id } = regulationsMap[subdomainName]
            return (
               <Accordion key={id} className={classes.accordionWidth}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                     <MaterialIcon fill="outlined" name={icon} size={16} />
                     <div className={classes.accordionMainTitle}>
                        {subdomainName}
                     </div>
                  </AccordionSummary>
                  <AccordionDetails className={classes.regulationsContainer}>
                     {regulations.map(regulation => (
                        <RegulationCard
                           key={regulation.id}
                           regulation={regulation}
                        />
                     ))}
                  </AccordionDetails>
               </Accordion>
            )
         })}
      </>
   )
}

// -------------------------

function RegulationsList({ regulationsList }) {
   const classes = useRegulationsStyles()

   if (!regulationsList.length) {
      return <NoResultsMessage />
   }

   return (
      <Grid className={classes.regulationsContainer}>
         {regulationsList.map(regulation => (
            <RegulationCard key={regulation.id} regulation={regulation} />
         ))}
      </Grid>
   )
}

// -----------------------------------

const usefilterStyles = makeStyles(theme => ({
   container: {
      borderTop: '4px solid #3264DC',
      padding: theme.spacing(1, 0, 1, 2),
      width: 180
   },
   title: {
      fontSize: 16,
      fontWeight: 'bold'
   }
}))

function RegulationsFiltersPopover({
   anchorEl,
   setAnchorEl,
   value,
   onFilterSelection
}) {
   const classes = usefilterStyles()
   const open = Boolean(anchorEl)
   const id = open ? 'simple-popover' : undefined

   function handleClose() {
      setAnchorEl(null)
   }

   function handleChange(event) {
      onFilterSelection(event.target.value)
   }

   return (
      <Popover
         id={id}
         open={open}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         transformOrigin={{ vertical: 'top', horizontal: 'right' }}
         anchorEl={anchorEl}
         onClose={handleClose}
      >
         <Grid className={classes.container}>
            <FormControl component="fieldset">
               <FormLabel component="legend" className={classes.title}>
                  Filtrar por
               </FormLabel>
               <RadioGroup
                  aria-label="Filtrar"
                  name="Filtrar1"
                  value={value}
                  onChange={handleChange}
               >
                  <FormControlLabel
                     value="subdomain"
                     control={<Radio size="small" />}
                     label="Subdominio"
                  />
                  <FormControlLabel
                     value="date"
                     control={<Radio size="small" />}
                     label="Fecha"
                  />
                  <FormControlLabel
                     value="unread"
                     control={<Radio size="small" />}
                     label="Mensaje Nuevo"
                  />
                  <FormControlLabel
                     value="read"
                     control={<Radio size="small" />}
                     label="Mensaje Leído"
                  />
               </RadioGroup>
            </FormControl>
         </Grid>
      </Popover>
   )
}

// ---------------------------------------------------

const useStyles = makeStyles(theme => ({
   search: {
      position: 'relative',
      border: '1px solid #3264DC',
      borderRadius: theme.shape.borderRadius,
      width: '100%'
   },
   searchIcon: {
      padding: theme.spacing(0, 1),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#3264DC'
   },
   inputRoot: {
      color: 'inherit',
      width: '100%'
   },
   inputInput: {
      padding: theme.spacing(1, 1, 1, 5),
      transition: theme.transitions.create('width'),
      width: '100%'
   }
}))

function SearchField({ onSearchCallback }) {
   const classes = useStyles()

   function onSearch(e) {
      if (e.keyCode == 13) {
         const query = e.target.value
         onSearchCallback(query, null, INITAL_PAGINATION_DATA)
      }
   }

   return (
      <div className={classes.search}>
         <div className={classes.searchIcon}>
            <SearchIcon />
         </div>
         <InputBase
            placeholder="Escriba y presione Enter para buscar"
            classes={{
               root: classes.inputRoot,
               input: classes.inputInput
            }}
            inputProps={{ 'aria-label': 'search' }}
            onKeyDown={onSearch}
         />
      </div>
   )
}
